/**
 * React Router configuration — all routes with lazy loading.
 * Pages are lazy-loaded for optimal code splitting.
 *
 * @see docs/DEPLOYMENT.md
 * @see Issue #87, #119 (404 page)
 */
import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, useRouteError } from "react-router-dom";
import { AppShell } from "./layouts/AppShell";
import { useAuthStore } from "./store/auth";
import { PageLoader } from "./components/PageLoader";

/**
 * Smart index redirect (#F3 behavior audit):
 * - Authenticated users → /topics (推荐 home feed)
 * - First-time visitors → /welcome (landing page, explains value)
 * - Returning visitors → /topics (they know the product)
 */
function IndexRedirect() {
  const authState = useAuthStore((s) => s.state);
  if (authState === "loading") return null;
  if (authState === "authenticated") return <Navigate to="/topics" replace />;
  // Check if user has visited before
  try {
    if (localStorage.getItem("ig-visited")) return <Navigate to="/topics" replace />;
  } catch { /* noop */ }
  return <Navigate to="/welcome" replace />;
}

// #543: Friendly error boundary for dynamic import failures (offline navigation)
function RouteErrorFallback() {
  const error = useRouteError();
  const isChunkError = error instanceof TypeError && (
    String(error.message).includes("dynamically imported module") ||
    String(error.message).includes("Failed to fetch")
  );
  if (isChunkError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
        <svg className="w-12 h-12 text-amber-500 mb-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
        </svg>
        <h2 className="text-base font-semibold text-slate-700 mb-2">网络连接不可用</h2>
        <p className="text-sm text-slate-500 mb-4">无法加载页面，请检查网络连接后重试</p>
        <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
          重新加载
        </button>
      </div>
    );
  }
  // Re-throw non-chunk errors for default handling
  throw error;
}

// Lazy-loaded pages for code splitting
const FollowPage = lazy(() => import("./pages/FollowPage"));
const TopicsPage = lazy(() => import("./pages/TopicsPage"));
const TopicDetailPage = lazy(() => import("./pages/TopicDetailPage"));
const TalkDetailPage = lazy(() => import("./pages/TalkDetailPage"));
const AtomEditPage = lazy(() => import("./pages/AtomEditPage"));
const DialoguePage = lazy(() => import("./pages/DialoguePage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const ClubPage = lazy(() => import("./pages/ClubPage"));
const AiTalkPage = lazy(() => import("./pages/AiTalkPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage"));
const OnboardingPage = lazy(() => import("./pages/OnboardingPage"));
const BookmarksPage = lazy(() => import("./pages/BookmarksPage"));
const WeeklyReviewPage = lazy(() => import("./pages/WeeklyReviewPage"));
const ThoughtMapPage = lazy(() => import("./pages/ThoughtMapPage"));
const ActivityPage = lazy(() => import("./pages/ActivityPage"));
const ExportPage = lazy(() => import("./pages/ExportPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const EnterprisePage = lazy(() => import("./pages/EnterprisePage"));

function PageSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<PageLoader message="加载页面..." />}>
      {children}
    </Suspense>
  );
}

export const router = createBrowserRouter([
  /* ── Standalone pages (no AppShell) ── */
  {
    path: "/welcome",
    element: <PageSuspense><LandingPage /></PageSuspense>,
  },
  {
    path: "/enterprise",
    element: <PageSuspense><EnterprisePage /></PageSuspense>,
  },
  /* #392: Auth pages outside AppShell — no nav tabs on login/signup */
  {
    path: "/login",
    element: <PageSuspense><LoginPage /></PageSuspense>,
  },
  {
    path: "/signup",
    element: <PageSuspense><SignupPage /></PageSuspense>,
  },
  {
    path: "/forgot-password",
    element: <PageSuspense><ForgotPasswordPage /></PageSuspense>,
  },
  {
    path: "/reset-password",
    element: <PageSuspense><ResetPasswordPage /></PageSuspense>,
  },
  {
    path: "/auth/callback",
    element: <PageSuspense><AuthCallbackPage /></PageSuspense>,
  },

  /* ── Main app (with AppShell) ── */
  {
    path: "/",
    element: <AppShell />,
    errorElement: <RouteErrorFallback />,
    children: [
      { index: true, element: <IndexRedirect /> },
      { path: "follow", element: <PageSuspense><FollowPage /></PageSuspense> },
      { path: "topics", element: <PageSuspense><TopicsPage /></PageSuspense> },
      { path: "topic/:id", element: <PageSuspense><TopicDetailPage /></PageSuspense> },
      { path: "idea/:id", element: <PageSuspense><TalkDetailPage /></PageSuspense> },
      { path: "talk/:id", element: <PageSuspense><TalkDetailPage /></PageSuspense> },
      { path: "talk/:id/edit", element: <PageSuspense><AtomEditPage /></PageSuspense> },
      { path: "dialogue/:id", element: <PageSuspense><DialoguePage /></PageSuspense> },
      { path: "profile/:id", element: <PageSuspense><ProfilePage /></PageSuspense> },
      /* #298: /profile redirects to /profile/me */
      { path: "profile", element: <Navigate to="/profile/me" replace /> },
      /* #298: /weekly-review alias */
      { path: "weekly-review", element: <Navigate to="/review" replace /> },
      { path: "club", element: <PageSuspense><ClubPage /></PageSuspense> },
      { path: "ai-talk", element: <PageSuspense><AiTalkPage /></PageSuspense> },
      { path: "messages", element: <PageSuspense><MessagesPage /></PageSuspense> },
      { path: "onboarding", element: <PageSuspense><OnboardingPage /></PageSuspense> },
      { path: "bookmarks", element: <PageSuspense><BookmarksPage /></PageSuspense> },
      { path: "review", element: <PageSuspense><WeeklyReviewPage /></PageSuspense> },
      { path: "thought-map", element: <PageSuspense><ThoughtMapPage /></PageSuspense> },
      { path: "activity", element: <PageSuspense><ActivityPage /></PageSuspense> },
      { path: "export", element: <PageSuspense><ExportPage /></PageSuspense> },
      /* #459: /settings not yet built — redirect to profile */
      { path: "settings", element: <Navigate to="/profile/me" replace /> },
      { path: "*", element: <PageSuspense><NotFoundPage /></PageSuspense> },
    ],
  },
]);